import React, { useContext, useEffect, useState } from "react";
import { Box, Button, IconButton, Paper, Typography } from "@mui/material";
import { AppContext } from "../../AppContextProvider";
import { Role } from "../../models/User";
import { Notification } from "../../models/Notification";
import { Delete, Edit, Grade } from "@mui/icons-material";
import { DateTimeUtils, DateTimeFormat } from "../../DateTimeUtils";
import { green } from "@mui/material/colors";
import EditNotificationDialog from "./EditNotificationDialog";
import ConfirmDialog from "../ConfirmDialog";
import {
  deleteNotification,
  updateNotificationReviewDate,
} from "../../api/CafeteriaClient";
import { AxiosError } from "axios";

const NotificationsList: React.FC<{
  title?: string;
  notifications: Notification[];
  onDelete: (notification: Notification) => void;
  onEdit: (notification: Notification) => void;
}> = ({ title, notifications, onDelete, onEdit }) => {
  const { user } = useContext(AppContext);

  useEffect(() => {
    if (user.id) {
      try {
        updateNotificationReviewDate();
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        return;
      }
    }
  }, [user]);

  if (!notifications.length) {
    return <></>;
  }

  const sortedNotifications = [...notifications].sort(
    (n1, n2) =>
      (n1.startDate.localeCompare(n2.startDate) ||
        n1.endDate.localeCompare(n2.endDate) ||
        n1.id - n2.id) * -1
  );

  return (
    <>
      {title ? <Typography variant="h5">{title}</Typography> : <></>}
      {sortedNotifications.map((notification) => (
        <Paper
          key={notification.id}
          sx={{ display: "grid", gridTemplateColumns: "1fr auto auto", p: 2 }}
        >
          <Box
            sx={{
              mb: 1,
              gridColumn: user.role !== Role.ADMIN ? "span 2" : undefined,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            {user.role !== Role.ADMIN &&
            new Date(user.notificationReviewDate) <
              new Date(notification.creationDate) ? (
              <Grade sx={{ color: green[300] }} />
            ) : (
              <></>
            )}
            <Typography fontWeight="bold">
              {user.role !== Role.ADMIN
                ? "Posted: " +
                  DateTimeUtils.toString(
                    notification.startDate,
                    DateTimeFormat.SHORT_DAY_OF_WEEK_DESC
                  )
                : "Post Dates: " +
                  DateTimeUtils.toString(
                    notification.startDate,
                    DateTimeFormat.SHORT_DAY_OF_WEEK_DESC
                  ) +
                  " - " +
                  DateTimeUtils.toString(
                    notification.endDate,
                    DateTimeFormat.SHORT_DAY_OF_WEEK_DESC
                  )}
            </Typography>
          </Box>
          {user.role === Role.ADMIN ? (
            <>
              <IconButton
                color="primary"
                onClick={() => onEdit(notification)}
                size="small"
              >
                <Edit />
              </IconButton>
              <IconButton
                color="primary"
                onClick={() => onDelete(notification)}
                size="small"
              >
                <Delete />
              </IconButton>
            </>
          ) : (
            <></>
          )}

          <Typography
            sx={{
              gridColumn: user.role !== Role.ADMIN ? "span 2" : undefined,
            }}
          >
            {notification.msg}
          </Typography>
        </Paper>
      ))}
    </>
  );
};

const NotificationsPage: React.FC = () => {
  const { user, notifications, setNotifications, setSnackbarErrorMsg } = useContext(AppContext);
  const [showNotificationDialog, setShowNotificationDialog] = useState(false);
  const [notificationToEdit, setNotificationToEdit] = useState<Notification>();
  const [notificationToDelete, setNotificationToDelete] =
    useState<Notification>();

  const today = DateTimeUtils.toString(DateTimeUtils.getCurrentDate());

  const handleEditNotification = (notification: Notification) => {
    setNotificationToEdit(notification);
    setShowNotificationDialog(true);
  };

  const handleCloseDialog = () => {
    setNotificationToEdit(undefined);
    setShowNotificationDialog(false);
  };

  const handleRequestDeleteNotification = (notification: Notification) => {
    setNotificationToDelete(notification);
  };

  const handleCancelDeleteNotification = () => {
    setNotificationToDelete(undefined);
  };

  const handleConfirmDeleteNotification = async () => {
    if (!notificationToDelete) {
      return;
    }
    const notification = notificationToDelete;
    setNotificationToDelete(undefined);
    try {
      await deleteNotification(notification.id);
      setNotifications(
        notifications.filter((item) => item.id !== notification.id)
      );
    } catch (error) {
      const axiosError = error as AxiosError;
      setSnackbarErrorMsg(
        "Error deleting notification: " +
        (axiosError.response?.data?.toString() ?? axiosError.response?.statusText ?? "Unknown server error")
      );
    }
  };

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography fontWeight='bold'>
        Notifications
      </Typography>
      <Box
        sx={{
          display: user.role !== Role.ADMIN ? "flex" : undefined,
          alignItems: "center",
          gap: 1,
        }}
      >
        {user.role !== Role.ADMIN ? (
          <>
            <Grade sx={{ color: green[300] }} />
            <Typography textAlign={"left"}>New Notifications</Typography>
          </>
        ) : (
          <Button
            variant="contained"
            onClick={() => setShowNotificationDialog(true)}
          >
            Add A Notification
          </Button>
        )}
      </Box>
      {user.role !== Role.ADMIN ? (
        <NotificationsList
          onDelete={handleRequestDeleteNotification}
          onEdit={handleEditNotification}
          notifications={notifications.filter(
            (notification) =>
              notification.startDate <= today && notification.endDate >= today
          )}
        />
      ) : (
        <>
          <NotificationsList
            title={"Current Notifications"}
            onDelete={handleRequestDeleteNotification}
            onEdit={handleEditNotification}
            notifications={notifications.filter(
              (notification) =>
                notification.startDate <= today && notification.endDate >= today
            )}
          />
          <NotificationsList
            title={"Upcoming Notifications"}
            onDelete={handleRequestDeleteNotification}
            onEdit={handleEditNotification}
            notifications={notifications.filter(
              (notification) => notification.startDate > today
            )}
          />
          <NotificationsList
            title={"Expired Notifications"}
            onDelete={handleRequestDeleteNotification}
            onEdit={handleEditNotification}
            notifications={notifications.filter(
              (notification) => notification.endDate < today
            )}
          />
        </>
      )}
      {showNotificationDialog ? (
        <EditNotificationDialog
          notification={notificationToEdit}
          onClose={handleCloseDialog}
        />
      ) : (
        <></>
      )}
      {notificationToDelete ? (
        <ConfirmDialog
          title="Delete Notification"
          open={true}
          okLabel="Delete"
          onOk={handleConfirmDeleteNotification}
          onCancel={handleCancelDeleteNotification}
        >
          <Typography>
            Are you sure you want to delete this notification?
          </Typography>
        </ConfirmDialog>
      ) : (
        <></>
      )}
    </Box>
  );
};

export default NotificationsPage;
